import React from 'react';

import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { cssMap } from '@atlaskit/css';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { type PersonProfileData } from '../../types';

import { TenureBar } from './TenureBar';

const messages = defineMessages({
	overviewManager: {
		id: 'user-profile-preview.overview.manager',
		defaultMessage: 'Manager',
		description: 'Label for the manager field in the profile overview section',
	},
	overviewTenure: {
		id: 'user-profile-preview.overview.tenure',
		defaultMessage: 'Tenure',
		description: 'Label for the tenure field in the profile overview section',
	},
	overviewEmail: {
		id: 'user-profile-preview.overview.email',
		defaultMessage: 'Email',
		description: 'Label for the email field in the profile overview section',
	},
	overviewOrganization: {
		id: 'user-profile-preview.overview.organization',
		defaultMessage: 'Organization',
		description: 'Label for the organization field in the profile overview section',
	},
	tenureYears: {
		id: 'user-profile-preview.overview.tenure-years',
		defaultMessage: '{count, plural, one {# Year} other {# Years}}',
		description: 'Formatted year count for tenure display',
	},
	tenureMonths: {
		id: 'user-profile-preview.overview.tenure-months',
		defaultMessage: '{count, plural, one {# month} other {# months}}',
		description: 'Formatted month count for tenure display',
	},
	tenureStarted: {
		id: 'user-profile-preview.overview.tenure-started',
		defaultMessage: 'Started {date}',
		description: 'Start date label for tenure display',
	},
});

const styles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		columnGap: token('space.300'),
		rowGap: token('space.050'),
		width: '100%',
	},
	label: {
		minWidth: '80px',
	},
	gridItem: {
		paddingTop: token('space.075'),
		paddingBottom: token('space.075'),
	},
	stackPadding: {
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingTop: token('space.150'),
	},
});

interface TenureData {
	beforePercent: number;
	beforeCount: number;
	afterCount: number;
}

export interface OverviewProps {
	data: PersonProfileData;
	tenureData?: TenureData;
	testId?: string;
	variant?: 'full' | 'compact';
}

function computeTenure(intl: IntlShape, startDateStr: string) {
	const startDate = new Date(startDateStr);
	const now = new Date();
	const diffMs = now.getTime() - startDate.getTime();
	const totalMonths = Math.floor(diffMs / (1000 * 60 * 60 * 24 * 30.44));
	const years = Math.floor(totalMonths / 12);
	const months = totalMonths % 12;

	const parts: string[] = [];
	if (years > 0) {
		parts.push(intl.formatMessage(messages.tenureYears, { count: years }));
	}
	if (months > 0 || years === 0) {
		parts.push(intl.formatMessage(messages.tenureMonths, { count: months }));
	}

	const formattedDate = intl.formatDate(startDate, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
	});

	return {
		duration: parts.join(' '),
		startLabel: intl.formatMessage(messages.tenureStarted, { date: formattedDate }),
	};
}

function hasNonEmptyString(value?: string | null): value is string {
	return typeof value === 'string' && value.trim().length > 0;
}

export function Overview({ data, tenureData, testId, variant = 'full' }: OverviewProps) {
	const intl = useIntl();
	const { name, email, organization, startDate, reportsTo } = data;

	const tenure = startDate ? computeTenure(intl, startDate) : null;
	const isCompact = variant === 'compact';

	const content = (
		<>
			{reportsTo ? (
				<Inline xcss={styles.gridItem} space="space.150">
					<Box xcss={styles.label}>
						<Text color="color.text.subtlest">{intl.formatMessage(messages.overviewManager)}</Text>
					</Box>
					<Inline space="space.100" alignBlock="center">
						<Avatar src={reportsTo.avatarUrl ?? undefined} name={reportsTo.name} size="small" />
						<Stack space="space.0">
							<Text>{reportsTo.name}</Text>
							{reportsTo.jobTitle ? (
								<Text size="small" color="color.text.subtlest">
									{reportsTo.jobTitle}
								</Text>
							) : null}
						</Stack>
					</Inline>
				</Inline>
			) : null}

			{tenure ? (
				<Inline xcss={styles.gridItem} space="space.150">
					<Box xcss={styles.label}>
						<Text color="color.text.subtlest">{intl.formatMessage(messages.overviewTenure)}</Text>
					</Box>
					<Stack space="space.050">
						<Text>{tenure.duration}</Text>
						<Text size="small" color="color.text.subtlest">
							{tenure.startLabel}
						</Text>
						{tenureData ? (
							<TenureBar
								beforePercent={tenureData.beforePercent}
								beforeCount={tenureData.beforeCount}
								afterCount={tenureData.afterCount}
								userName={name}
							/>
						) : null}
					</Stack>
				</Inline>
			) : null}

			{hasNonEmptyString(email) ? (
				<Inline xcss={styles.gridItem} space="space.150">
					<Box xcss={styles.label}>
						<Text color="color.text.subtlest">{intl.formatMessage(messages.overviewEmail)}</Text>
					</Box>
					<Text>{email}</Text>
				</Inline>
			) : null}

			{hasNonEmptyString(organization) ? (
				<Inline xcss={styles.gridItem} space="space.150">
					<Box xcss={styles.label}>
						<Text color="color.text.subtlest">
							{intl.formatMessage(messages.overviewOrganization)}
						</Text>
					</Box>
					<Text>{organization}</Text>
				</Inline>
			) : null}
		</>
	);

	if (isCompact) {
		return (
			<Stack xcss={styles.stackPadding} space="space.100" testId={testId}>
				{content}
			</Stack>
		);
	}

	return (
		<Box xcss={styles.grid} testId={testId}>
			{content}
		</Box>
	);
}
